import React, { useState } from 'react'
import { styled } from '@mui/material/styles'
import clsx from 'clsx'
import { IconButton } from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ThemeConfig from '../../../theme/ThemeConfig'
import Typography from '../../Typography/Typography'
import { GroupType } from '../types/GroupType'
import { AlertType } from '../types/AlertType'
import { Alert, FieldId } from '../types/Alert'

const PREFIX = 'GroupedAlerts'

const classes = {
  summaryContainer: `${PREFIX}-summaryContainer`,
  detailsContainer: `${PREFIX}-detailsContainer`,
  expandIcon: `${PREFIX}-expandIcon`,
  groupingCount: `${PREFIX}-groupingCount`,
  detailsGroupingCount: `${PREFIX}-detailsGroupingCount`,
  summaryPanel: `${PREFIX}-summaryPanel`,
  summaryContent: `${PREFIX}-summaryContent`,
  expansionButton: `${PREFIX}-expansionButton`,
  expanded: `${PREFIX}-expanded`,
  notClickable: `${PREFIX}-notClickable`,
}

const Root = styled('tr', {
  shouldForwardProp: prop => prop !== 'alertType',
})<StyleProps>(({ theme, alertType }) => ({
  [`&.${classes.summaryContainer}`]: {
    borderBottom: '1px solid',
    borderBottomColor: ThemeConfig.palette.secondary.main,
    '&$expanded': {
      borderBottomColor: ThemeConfig.palette.secondary.light,
    },
    '&:hover': {
      backgroundColor: hoverColor,
      cursor: 'pointer',
    },
  },

  [`& .${classes.detailsContainer}`]: {
    borderBottom: '1px solid',
    borderBottomColor: ThemeConfig.palette.secondary.main,
    '&$expanded': {
      borderBottomColor: ThemeConfig.palette.secondary.light,
    },
    '&:hover': {
      backgroundColor: hoverColor,
      cursor: 'pointer',
    },
    '&$notClickable': {
      cursor: 'auto',
    },
  },

  [`& .${classes.expandIcon}`]: {
    transform: 'rotate(0deg)',
    transition: theme.transitions.create('transform', {
      duration: theme.transitions.duration.shortest,
    }),
    '&$expanded': {
      transform: 'rotate(180deg)',
    },
  },

  [`& .${classes.groupingCount}`]: {
    ...groupingCountTemplate,
    borderRight:
      '2px solid ' +
      (alertType === AlertType.Warning
        ? ThemeConfig.palette.warning.main
        : ThemeConfig.palette.error.main),
  },

  [`& .${classes.detailsGroupingCount}`]: {
    ...groupingCountTemplate,
  },

  [`& .${classes.summaryPanel}`]: {
    padding: '8px 5px 8px 8px',
    borderBottom: 'none',
    maxWidth: '220px',
    width: '100%',
    overflowWrap: 'break-word',
  },

  [`& .${classes.summaryContent}`]: {},

  [`& .${classes.expansionButton}`]: {
    padding: '0px 5px 0px 0px',
    borderBottom: 'none',
    '&:last-child': {
      paddingRight: '5px',
    },
  },

  // Required for correct rendering
  [`&.${classes.expanded}`]: {},

  [`& .${classes.notClickable}`]: {},
}))

const hoverColor = '#F7F7F7'

interface StyleProps {
  alertType?: AlertType
}

const groupingCountTemplate = {
  padding: '8px',
  borderBottom: 'none',
}

export interface GroupedAlertsProps {
  groupType: GroupType.Product | GroupType.FieldAndMessage
  alertType: AlertType
  alerts: Alert[]
  onAlertClick?: (args: { fieldId: string; productId: string }) => void
  expandedByDefault?: boolean
}

const TableCell = ({ style = {}, ...props }) => (
  <td {...props} style={{ verticalAlign: 'middle', ...(style ?? {}) }} />
)

const getAlertClick = (
  alert: Alert,
  onAlertClick?: (args: { fieldId: string; productId: string }) => void,
): (() => void) | undefined => {
  if (onAlertClick === undefined || alert.fieldId === FieldId.Unknown) {
    return undefined
  }

  return () =>
    onAlertClick?.({
      fieldId: alert.fieldId as string,
      productId: alert.productId,
    })
}

export const GroupedAlerts = ({
  groupType,
  alertType,
  alerts = [],
  onAlertClick,
  expandedByDefault = false,
}: GroupedAlertsProps) => {
  const [expanded, setExpanded] = useState(expandedByDefault)

  const getCountLabel = () => {
    const suffix = alerts.length > 1 ? 's' : ''
    if (groupType === GroupType.Product) {
      if (alertType === AlertType.Error) {
        return `Error${suffix}`
      } else if (alertType === AlertType.Warning) {
        return `Warning${suffix}`
      }
    } else if (
      alertType === AlertType.Error ||
      alertType === AlertType.Warning
    ) {
      return `Product${suffix}`
    }

    return `Alert${suffix}`
  }

  return (
    <>
      {alerts.length > 0 && (
        <>
          <Root
            className={clsx(classes.summaryContainer, {
              [classes.expanded]: expanded,
            })}
            onClick={() => setExpanded(!expanded)}
            alertType={alertType}
          >
            <TableCell className={classes.groupingCount} align="center">
              <Typography variant="body1">
                <strong>{alerts.length}</strong>
              </Typography>
              <Typography variant="body2">{getCountLabel()}</Typography>
            </TableCell>
            <TableCell className={classes.summaryPanel}>
              <Typography variant="body1">
                {groupType === GroupType.Product &&
                  alerts[0].productDescription}
                {groupType === GroupType.FieldAndMessage && alerts[0].fieldName}
              </Typography>
              <Typography
                variant="body2"
                style={{ color: ThemeConfig.palette.secondary.dark }}
              >
                {groupType === GroupType.Product &&
                  `${alerts[0].productDisplayIdLabel}: ${alerts[0].productDisplayId}`}
                {groupType === GroupType.FieldAndMessage && alerts[0].message}
              </Typography>
            </TableCell>
            <TableCell className={classes.expansionButton} align="center">
              <IconButton
                aria-label="expand"
                className={clsx(classes.expandIcon, {
                  [classes.expanded]: expanded,
                })}
                size="small"
              >
                <ExpandMoreIcon />
              </IconButton>
            </TableCell>
          </Root>
          {expanded &&
            groupType === GroupType.Product &&
            alerts.map((alert, idx) => (
              <GroupedAlertsDetail
                key={`productDetail-${alert.productId}-${alert.fieldId}-${alert.message}`}
                groupType={groupType}
                name={alert.fieldName}
                message={alert.message}
                onClick={getAlertClick(alert, onAlertClick)}
                last={idx === alerts.length - 1}
              />
            ))}
          {expanded &&
            groupType === GroupType.FieldAndMessage &&
            alerts.map((alert, idx) => (
              <GroupedAlertsDetail
                key={`fieldDetail-${alert.fieldId}-${alert.message}-${alert.productId}`}
                groupType={groupType}
                name={alert.productDescription}
                message={`${alert.productDisplayIdLabel}: ${alert.productDisplayId}`}
                onClick={getAlertClick(alert, onAlertClick)}
                last={idx === alerts.length - 1}
              />
            ))}
        </>
      )}
    </>
  )
}

const GroupedAlertsDetail = ({
  groupType,
  name,
  message,
  onClick,
  last,
}: {
  groupType: GroupType.Product | GroupType.FieldAndMessage
  name?: string
  message: string
  onClick?: () => void
  last: boolean
}) => {
  return (
    <tr
      onClick={onClick}
      className={clsx(classes.detailsContainer, {
        [classes.expanded]: !last, // Expanded class has a lighter separator line, so the last one should not have it
        [classes.notClickable]: onClick === undefined,
      })}
    >
      <TableCell className={classes.detailsGroupingCount} />
      <TableCell className={classes.summaryPanel}>
        <div className={classes.summaryContent}>
          {name !== undefined && (
            <Typography
              variant="body1"
              style={{
                color: ThemeConfig.palette.primary.main,
                textDecoration: 'underline',
              }}
              component="span"
            >
              {name}
            </Typography>
          )}
          {groupType === GroupType.Product && (
            // Field Details
            <Typography
              variant="body2"
              style={{ color: ThemeConfig.palette.secondary.dark }}
              component="span"
            >
              {name !== undefined && ' - '}
              {message}
            </Typography>
          )}
          {groupType === GroupType.FieldAndMessage && (
            // Product Details
            <Typography
              variant="body2"
              style={{ color: ThemeConfig.palette.secondary.dark }}
            >
              {message}
            </Typography>
          )}
        </div>
      </TableCell>
      <TableCell className={classes.expansionButton}></TableCell>
    </tr>
  )
}
